/**
 * MatrixRainSurface Component
 *
 * Renders a canvas with falling Matrix-style green character columns.
 * APRIL FOOLS 2026: Remove after April 1 (along with the MatrixRain folder).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MatrixRainSurfaceProps } from './types';
import { useMatrixRainColumns } from './useMatrixRainColumns';
import { useSnowfallToggle } from '../Snowfall/SnowfallContext';
import { drawMatrixColumn } from './drawMatrixColumn';

export function MatrixRainSurface({
  width,
  height,
  intensity = 'medium',
  fontSize = 14,
  respectToggle = true,
  opaqueBackground = false,
  className,
  style,
}: MatrixRainSurfaceProps) {
  const { enabled: contextEnabled } = useSnowfallToggle();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const isEnabled = respectToggle ? contextEnabled : true;

  // Measure actual monospace character width on first render
  const cellWidth = fontSize * 0.6;
  const cellHeight = fontSize * 1.2;

  const columns = useMatrixRainColumns({
    intensity,
    canvasWidth: width,
    canvasHeight: height,
    fontSize,
    cellWidth,
    cellHeight,
  });

  const numRows = Math.max(1, Math.floor(height / cellHeight));

  // Initialize when dimensions are ready
  useEffect(() => {
    if (!isEnabled || width === 0 || height === 0) {
      return undefined;
    }

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    // Set canvas resolution for sharp rendering
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Measure actual monospace character width for accuracy
    ctx.font = `${fontSize}px monospace`;

    columns.init();
    setIsInitialized(true);

    return () => {
      columns.reset();
      setIsInitialized(false);
    };
  }, [isEnabled, width, height, fontSize, columns]);

  // Animation render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    columns.update();

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Optional opaque background for standalone use
    if (opaqueBackground) {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
    }

    // Draw each active column
    for (const col of columns.getColumns()) {
      drawMatrixColumn(ctx, col, cellWidth, cellHeight, fontSize, numRows);
    }

    animationFrameRef.current = requestAnimationFrame(render);
  }, [
    width,
    height,
    columns,
    cellWidth,
    cellHeight,
    fontSize,
    numRows,
    opaqueBackground,
  ]);

  // Start/stop animation loop
  useEffect(() => {
    if (!isEnabled || !isInitialized) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return undefined;
    }

    animationFrameRef.current = requestAnimationFrame(render);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isEnabled, isInitialized, render]);

  if (!isEnabled) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width,
        height,
        pointerEvents: 'none',
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width,
          height,
          display: 'block',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />
    </div>
  );
}
